import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Save, X, Upload } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { FileText } from 'lucide-react';
import { Confirm, useNotify } from 'react-admin';
import * as XLSX from 'xlsx';
import SpecDataDetail from './SpecDataDetail.tsx';

declare const process: {
	env: {
		REACT_APP_API_URL?: string;
	};
};

export interface SpecificationItem {
	id?: number;
	batchNumber?: string;
	parameter: string;
	classification: string;
	testMethod: string;
	uom: string;
	specMin: number | string | null;
	specMax: number | string | null;
	specMinKJ?: number | string | null;
	specMaxKJ?: number | string | null;
}

export interface Specification {
	id: string;
	batchNumber: string;
	productDescription: string;
	manufacturer?: string | null;
	productType?: string | null;
	countryOfOrigin?: string | null;
	specNumber?: string | null;
	reportNumber?: string | null;
	sampleSubmissionRefNumber?: string | null;
	sampleReceivedDate?: string | null;
	packaging?: string | null;
	testingPurpose?: string | null;
	client?: string | null;
	manufactureDate?: string | null;
	expiryDate?: string | null;
	items: SpecificationItem[];
}

interface SpecDataListProps {
	specifications: Specification[];
	onUpdate: (specs: Specification[]) => void;
}

type SpecImportType = 'normal' | 'special';

export default function SpecDataList({ specifications = [], onUpdate }: SpecDataListProps) {
	const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
	const [isAdding, setIsAdding] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [formData, setFormData] = useState<Omit<Specification, 'id'>>({
		batchNumber: '',
		productDescription: '',
		specNumber: '',
		sampleSubmissionRefNumber: '',
		manufactureDate: '',
		expiryDate: '',
		items: []
	});
	const [newItem, setNewItem] = useState<SpecificationItem>({
		parameter: '',
		classification: 'General',
		testMethod: '',
		uom: '',
		specMin: null,
		specMax: null,
		specMinKJ: null,
		specMaxKJ: null,
	});

	const [specs, setSpecs] = useState<Specification[]>(specifications || []);
	const [page, setPage] = useState(1);
	const [perPage, setPerPage] = useState(10);
	const [batchNumberQuery, setBatchNumberQuery] = useState('');
	const [listFilter, setListFilter] = useState<{ batchNumber: string } | null>(null);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [loadingList, setLoadingList] = useState(false);
	const notify = useNotify();
	const [saving, setSaving] = useState(false);
	const [importing, setImporting] = useState(false);
	const importFileInputRef = useRef<HTMLInputElement | null>(null);
	const [importModalOpen, setImportModalOpen] = useState(false);
	const [importTemplateType, setImportTemplateType] = useState<SpecImportType>('normal');
	const [importFile, setImportFile] = useState<File | null>(null);
	const [detailSpec, setDetailSpec] = useState<Specification | null>(null);
	const [loadingDetail, setLoadingDetail] = useState(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

	const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/[_\s\-/()]+/g, '');

	const extractClassificationName = (value: string): string => {
		const text = String(value ?? '').trim();
		if (!text) return 'General';

		const firstLine = text.split(/\r?\n/)[0]?.trim() || text;
		return firstLine.replace(/[\s:;，、。]+$/g, '') || 'General';
	};

	type SpecRangeValue = number | string | null;

	const readCell = (sheet: XLSX.WorkSheet, rowIndex: number, columnIndex: number): XLSX.CellObject | undefined => {
		const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
		const actualRow = range.s.r + rowIndex;
		const actualCol = range.s.c + columnIndex;
		return sheet[XLSX.utils.encode_cell({ r: actualRow, c: actualCol })] as XLSX.CellObject | undefined;
	};

	const parseSpecRangeValue = (cell: XLSX.CellObject | undefined): SpecRangeValue => {
		if (!cell) return null;

		const rawValue = cell.v;
		const formattedValue = typeof cell.w === 'string' ? cell.w.trim() : '';
		if (formattedValue) {
			// Keep Excel display text so ratios and decimal precision (e.g. 5:1, 14.0, 3.00) are preserved.
			return formattedValue;
		}

		if (typeof rawValue === 'number') {
			return Number.isFinite(rawValue) ? String(rawValue) : null;
		}

		if (typeof rawValue === 'string') {
			const text = rawValue.trim();
			return text || null;
		}

		return null;
	};

	const toIsoDate = (value: unknown): string => {
		if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
		if (typeof value === 'number') {
			if (!Number.isFinite(value) || value <= 0) return '';
			const jsDate = XLSX.SSF.parse_date_code(value);
			if (jsDate) {
				const d = new Date(jsDate.y, jsDate.m - 1, jsDate.d);
				if (d.getFullYear() < 1900) return '';
				return d.toISOString().slice(0, 10);
			}
		}
		if (typeof value === 'string') {
			const trimmed = value.trim();
			if (!trimmed) return '';
			const normalized = trimmed.replace(/\./g, '/').replace(/-/g, '/');
			const parts = normalized.split('/').map((p) => p.trim());
			if (parts.length === 3) {
				const y = Number(parts[0]);
				const m = Number(parts[1]);
				const d = Number(parts[2]);
				if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
					const date = new Date(y, m - 1, d);
					if (!Number.isNaN(date.getTime())) return date.toISOString().slice(0, 10);
				}
			}
			const date = new Date(trimmed);
			if (!Number.isNaN(date.getTime())) return date.toISOString().slice(0, 10);
		}
		return '';
	};

	// endRow: last row index to scan (inclusive, 0-based). Defaults to 59.
	const findLabeledValue = (rows: any[][], label: string, endRow = 59): unknown => {
		const target = normalizeHeader(label);
		for (let r = 0; r < Math.min(rows.length, endRow + 1); r++) {
			const row = rows[r] || [];
			for (let c = 0; c < row.length; c++) {
				const current = normalizeHeader(String(row[c] ?? ''));
				if (current.includes(target)) {
					for (let next = c + 1; next <= Math.min(c + 4, row.length - 1); next++) {
						const value = row[next];
						if (String(value ?? '').trim()) return value;
					}
				}
			}
		}
		return '';
	};

	const parseAnalyticalResultSheet = (sheet: XLSX.WorkSheet, isSpecialTemplate = false): Omit<Specification, 'id'> => {
		const rows = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '' }) as any[][];
		const parameterHeaderCandidates = [
			'parameter',
			'chemistry',
			'mineral',
			'vitamin',
			'optionalingredients',
			'microbiological',
			'sensoryphysicalfunctional',
			'contaminants',
		];

		let headerRowIndex = -1;
		let headerMap: Record<string, number> = {};
		for (let r = 0; r < Math.min(rows.length, 120); r++) {
			const row = rows[r] || [];
			const map = row.reduce<Record<string, number>>((acc, cell, index) => {
				const normalized = normalizeHeader(String(cell ?? ''));
				if (normalized) acc[normalized] = index;
				return acc;
			}, {});

			const hasMethod = typeof map.testmethod === 'number' || typeof map.method === 'number';
			const hasUom = typeof map.uom === 'number' || typeof map.unit === 'number';
			const hasMin = Object.keys(map).some((k) => k.includes('specmin'));
			const hasMax = Object.keys(map).some((k) => k.includes('specmax'));
			if (hasMethod && hasUom && hasMin && hasMax) {
				headerRowIndex = r;
				headerMap = map;
				break;
			}
		}

		if (headerRowIndex < 0) {
			throw new Error('Unable to locate table header row (Parameter/Test Method/UOM/Spec Min/Spec Max).');
		}

		// Metadata is scanned up to row 11 for special template, row 59 for normal.
		const metaEndRow = isSpecialTemplate ? Math.min(headerRowIndex - 1, 11) : 59;

		const productDescriptionRaw =
			findLabeledValue(rows, 'Product Description', metaEndRow) ||
			(isSpecialTemplate ? findLabeledValue(rows, 'Product Name', metaEndRow) : '');
		const specNumberRaw = findLabeledValue(rows, 'Spec Number', metaEndRow);
		const batchNumberRaw = findLabeledValue(rows, 'Batch Number', metaEndRow);
		const sampleSubmissionRefRaw = findLabeledValue(rows, 'Sample Submission Ref Number', metaEndRow);
		const manufactureDateRaw = findLabeledValue(rows, 'Manufacture Date', metaEndRow);
		const expiryDateRaw = findLabeledValue(rows, 'Expiry Date', metaEndRow);

		// Additional metadata fields present in the special template layout.
		const manufacturerRaw = isSpecialTemplate ? findLabeledValue(rows, 'Manufacturer', metaEndRow) : '';
		const productTypeRaw = isSpecialTemplate ? findLabeledValue(rows, 'Product Type', metaEndRow) : '';
		const countryOfOriginRaw = isSpecialTemplate ? findLabeledValue(rows, 'Country of Origin', metaEndRow) : '';
		const reportNumberRaw = isSpecialTemplate ? findLabeledValue(rows, 'Report Number', metaEndRow) : '';
		const sampleReceivedDateRaw = isSpecialTemplate ? findLabeledValue(rows, 'Sample Received Date', metaEndRow) : '';
		const packagingRaw = isSpecialTemplate ? findLabeledValue(rows, 'Packaging', metaEndRow) : '';
		const testingPurposeRaw = isSpecialTemplate ? findLabeledValue(rows, 'Testing Purpose', metaEndRow) : '';
		const clientRaw = isSpecialTemplate ? findLabeledValue(rows, 'Client', metaEndRow) : '';

		const parameterIdxFromHeader = (() => {
			for (const [key, idx] of Object.entries(headerMap)) {
				if (
					key.includes('parameter') ||
					key.includes('chemistry') ||
					parameterHeaderCandidates.includes(key)
				) {
					return idx;
				}
			}
			return -1;
		})();

		const testMethodIdx = headerMap.testmethod ?? headerMap.method ?? -1;
		const uomIdx = headerMap.uom ?? headerMap.unit ?? -1;

		// Prefer the /100g column over the /100kJ one for the primary specMin/Max.
		const specMinIdx = (
			Object.entries(headerMap).find(([k]) => k.includes('specmin') && !k.includes('kj')) ??
			Object.entries(headerMap).find(([k]) => k.includes('specmin'))
		)?.[1] ?? -1;
		const specMaxIdx = (
			Object.entries(headerMap).find(([k]) => k.includes('specmax') && !k.includes('kj')) ??
			Object.entries(headerMap).find(([k]) => k.includes('specmax'))
		)?.[1] ?? -1;

		// KJ columns (only present in the special template layout).
		const specMinKJIdx = Object.entries(headerMap).find(([k]) => k.includes('specmin') && k.includes('kj'))?.[1] ?? -1;
		const specMaxKJIdx = Object.entries(headerMap).find(([k]) => k.includes('specmax') && k.includes('kj'))?.[1] ?? -1;

		const parameterIdx =
			parameterIdxFromHeader >= 0
				? parameterIdxFromHeader
				: (() => {
						const headerRow = rows[headerRowIndex] || [];
						const limit = testMethodIdx > 0 ? testMethodIdx : headerRow.length;
						for (let i = 0; i < limit; i++) {
							if (String(headerRow[i] ?? '').trim()) return i;
						}
						return 0;
					})();

		const headerParameterText = String((rows[headerRowIndex] || [])[parameterIdx] ?? '').trim();
		let currentClassification = extractClassificationName(headerParameterText);

		const items: SpecificationItem[] = [];
		let emptyRowStreak = 0;
		for (let i = headerRowIndex + 1; i < rows.length; i++) {
			const row = rows[i] || [];
			const parameter = parameterIdx >= 0 ? String(row[parameterIdx] ?? '').trim() : '';
			const testMethod = testMethodIdx >= 0 ? String(row[testMethodIdx] ?? '').trim() : '';
			const uom = uomIdx >= 0 ? String(row[uomIdx] ?? '').trim() : '';
			const specMinCell = specMinIdx >= 0 ? readCell(sheet, i, specMinIdx) : undefined;
			const specMaxCell = specMaxIdx >= 0 ? readCell(sheet, i, specMaxIdx) : undefined;
			const specMin = parseSpecRangeValue(specMinCell);
			const specMax = parseSpecRangeValue(specMaxCell);

			const specMinKJCell = specMinKJIdx >= 0 ? readCell(sheet, i, specMinKJIdx) : undefined;
			const specMaxKJCell = specMaxKJIdx >= 0 ? readCell(sheet, i, specMaxKJIdx) : undefined;
			const specMinKJ = parseSpecRangeValue(specMinKJCell);
			const specMaxKJ = parseSpecRangeValue(specMaxKJCell);

			const normalizedTestMethod = normalizeHeader(testMethod);
			const normalizedUom = normalizeHeader(uom);
			const isSectionHeaderRow =
				!!parameter &&
				(normalizedTestMethod === 'testmethod' || normalizedTestMethod === 'method') &&
				(normalizedUom === 'uom' || normalizedUom === 'unit');

			if (isSectionHeaderRow) {
				currentClassification = extractClassificationName(parameter);
				continue;
			}

			if (!parameter && !testMethod && !uom && specMin === null && specMax === null) {
				emptyRowStreak += 1;
				if (items.length > 0 && emptyRowStreak >= 6) break;
				continue;
			}

			// Skip wrapped/continued rows that only carry a method-like value without a parameter or spec bounds.
			if (!parameter && !!testMethod && !uom && specMin === null && specMax === null) {
				continue;
			}

			emptyRowStreak = 0;

			items.push({
				parameter: parameter || testMethod || 'N/A',
				classification: currentClassification,
				testMethod: testMethod || parameter || 'N/A',
				uom: uom || '-',
				specMin,
				specMax,
				specMinKJ: specMinKJIdx >= 0 ? specMinKJ : null,
				specMaxKJ: specMaxKJIdx >= 0 ? specMaxKJ : null,
			});
		}

		return {
			productDescription: String(productDescriptionRaw ?? '').trim(),
			specNumber: String(specNumberRaw ?? '').trim(),
			batchNumber: String(batchNumberRaw ?? '').trim(),
			sampleSubmissionRefNumber: String(sampleSubmissionRefRaw ?? '').trim(),
			manufactureDate: toIsoDate(manufactureDateRaw),
			expiryDate: toIsoDate(expiryDateRaw),
			...(isSpecialTemplate ? {
				manufacturer: String(manufacturerRaw ?? '').trim() || null,
				productType: String(productTypeRaw ?? '').trim() || null,
				countryOfOrigin: String(countryOfOriginRaw ?? '').trim() || null,
				reportNumber: String(reportNumberRaw ?? '').trim() || null,
				sampleReceivedDate: toIsoDate(sampleReceivedDateRaw) || null,
				packaging: String(packagingRaw ?? '').trim() || null,
				testingPurpose: String(testingPurposeRaw ?? '').trim() || null,
				client: String(clientRaw ?? '').trim() || null,
			} : {}),
			items,
		};
	};

	const fetchSpecs = useCallback(async (p: number, pp: number, filterObj: any = null) => {
		setLoadingList(true);
		try {
			const start = (p - 1) * pp;
			const end = start + pp - 1;
			const params = new URLSearchParams();
			params.set('range', `[${start},${end}]`);
			params.set('perPage', String(pp));
			if (filterObj) params.set('filter', JSON.stringify(filterObj));

			const token = localStorage.getItem('token');
			const res = await fetch(`${API_URL}/specifications?${params.toString()}`, {
				method: 'GET',
				headers: {
					...(token ? { Authorization: `Bearer ${token}` } : {})
				}
			});

			if (!res.ok) throw new Error(`HTTP ${res.status}`);

			const list = await res.json();
			const contentRange = res.headers.get('Content-Range') || '';
			const m = /\/(\d+)$/.exec(contentRange);
			const total = m ? parseInt(m[1], 10) : list.length;

			setSpecs(list || []);
			setTotalCount(total);
			setPage(p);
			setPerPage(pp);
		} catch (err) {
			console.error('Failed to load specs', err);
		} finally {
			setLoadingList(false);
		}
	}, [API_URL]);

	useEffect(() => {
		fetchSpecs(page, perPage, listFilter);
	}, [fetchSpecs, page, perPage, listFilter]);

	useEffect(() => {
		const debounceTimer = window.setTimeout(() => {
			const keyword = batchNumberQuery.trim();
			setPage(1);
			setListFilter(keyword ? { batchNumber: keyword } : null);
		}, 300);

		return () => window.clearTimeout(debounceTimer);
	}, [batchNumberQuery]);

	const handleAddItem = () => {
		if (newItem.parameter && newItem.classification && newItem.testMethod && newItem.uom) {
			const itemToAdd: SpecificationItem = {
				parameter: newItem.parameter,
				classification: newItem.classification,
				testMethod: newItem.testMethod,
				uom: newItem.uom,
				specMin: newItem.specMin,
				specMax: newItem.specMax,
				specMinKJ: newItem.specMinKJ,
				specMaxKJ: newItem.specMaxKJ,
			};

			setFormData({
				...formData,
				items: [...formData.items, itemToAdd]
			});
			setNewItem({
				parameter: '',
				classification: 'General',
				testMethod: '',
				uom: '',
				specMin: null,
				specMax: null,
				specMinKJ: null,
				specMaxKJ: null,
			});
		}
	};

	const handleRemoveItem = (index: number) => {
		setFormData({
			...formData,
			items: formData.items.filter((_, i) => i !== index)
		});
	};

	const handleEditSpecification = (spec: Specification) => {
		setIsAdding(true);
		setEditingId(spec.id);
		setFormData({
			batchNumber: spec.batchNumber || '',
			productDescription: spec.productDescription || '',
			manufacturer: spec.manufacturer || '',
			productType: spec.productType || '',
			countryOfOrigin: spec.countryOfOrigin || '',
			specNumber: spec.specNumber || '',
			reportNumber: spec.reportNumber || '',
			sampleSubmissionRefNumber: spec.sampleSubmissionRefNumber || '',
			sampleReceivedDate: spec.sampleReceivedDate || '',
			packaging: spec.packaging || '',
			testingPurpose: spec.testingPurpose || '',
			client: spec.client || '',
			manufactureDate: spec.manufactureDate ? spec.manufactureDate.slice(0, 10) : '',
			expiryDate: spec.expiryDate ? spec.expiryDate.slice(0, 10) : '',
			items: (spec.items || []).map((item) => ({
				id: item.id,
				batchNumber: item.batchNumber,
				parameter: item.parameter || '',
				classification: item.classification || 'General',
				testMethod: item.testMethod || '',
				uom: item.uom || '',
				specMin: item.specMin ?? null,
				specMax: item.specMax ?? null,
				specMinKJ: item.specMinKJ ?? null,
				specMaxKJ: item.specMaxKJ ?? null,
			})),
		});
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const handleSave = useCallback(async () => {
		const hasRequiredMasterFields = formData.batchNumber && formData.productDescription;

		if (!hasRequiredMasterFields) {
			notify('Please fill batch number and product description', { type: 'warning' });
			return;
		}

		if (!editingId && formData.items.length === 0) {
			notify('Please add at least one test item', { type: 'warning' });
			return;
		}

		setSaving(true);
		try {
			const dto = {
				BatchNumber: formData.batchNumber,
				ProductDescription: formData.productDescription,
				Manufacturer: formData.manufacturer ? formData.manufacturer : null,
				ProductType: formData.productType ? formData.productType : null,
				CountryOfOrigin: formData.countryOfOrigin ? formData.countryOfOrigin : null,
				SpecNumber: formData.specNumber,
				ReportNumber: formData.reportNumber ? formData.reportNumber : null,
				SampleSubmissionRefNumber: formData.sampleSubmissionRefNumber,
				SampleReceivedDate: formData.sampleReceivedDate ? formData.sampleReceivedDate : null,
				Packaging: formData.packaging ? formData.packaging : null,
				TestingPurpose: formData.testingPurpose ? formData.testingPurpose : null,
				Client: formData.client ? formData.client : null,
				ManufactureDate: formData.manufactureDate ? formData.manufactureDate : null,
				ExpiryDate: formData.expiryDate ? formData.expiryDate : null,
				Items: formData.items.map(i => ({
					Parameter: i.parameter,
					Classification: i.classification,
					TestMethod: i.testMethod,
					Uom: i.uom,
					SpecMin: i.specMin ?? null,
					SpecMax: i.specMax ?? null,
					SpecMinKJ: i.specMinKJ ?? null,
					SpecMaxKJ: i.specMaxKJ ?? null,
				}))
			};

			const token = localStorage.getItem('token');

			let res: Response;
			if (editingId) {
				res = await fetch(`${API_URL}/specifications/${editingId}`, {
					method: 'PUT',
					headers: {
						'Content-Type': 'application/json',
						...(token ? { Authorization: `Bearer ${token}` } : {})
					},
					body: JSON.stringify(dto)
				});
			} else {
				res = await fetch(`${API_URL}/specifications`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						...(token ? { Authorization: `Bearer ${token}` } : {})
					},
					body: JSON.stringify(dto)
				});
			}

			if (!res.ok) {
				const text = await res.text();
				throw new Error(text || `HTTP ${res.status}`);
			}

			const returned = await res.json().catch(() => null);

			if (editingId) {
				if (typeof onUpdate === 'function') {
					onUpdate(specifications.map(s => s.id === editingId ? { ...(returned ?? { id: editingId }), ...formData } : s));
				}
				setEditingId(null);
			} else {
				const newSpec: Specification = {
					id: (returned && returned.id) ? String(returned.id) : Date.now().toString(),
					...formData
				};
				if (typeof onUpdate === 'function') {
					onUpdate([...specifications, newSpec]);
				}
			}

			notify('Save successful', { type: 'success' });
			try {
				fetchSpecs(page, perPage, listFilter);
			} catch (e) {
			}
			setIsAdding(false);
			setFormData({
				batchNumber: '',
				productDescription: '',
				specNumber: '',
				sampleSubmissionRefNumber: '',
				manufactureDate: '',
				expiryDate: '',
				items: [],
			});
		} catch (error: any) {
			console.error('Save spec error', error);
			notify(`Save failed: ${error?.message || 'Unknown error'}`, { type: 'error' });
		} finally {
			setSaving(false);
		}
	}, [API_URL, formData, editingId, specifications, onUpdate, notify, fetchSpecs, page, perPage, listFilter]);

	const handleDeleteClick = (id: string) => {
		setPendingDeleteId(id);
		setDeleteConfirmOpen(true);
	};

	const handleConfirmDelete = () => {
		const id = pendingDeleteId;
		if (!id) return;

		(async () => {
				try {
					const token = localStorage.getItem('token');
					const res = await fetch(`${API_URL}/specifications/${id}`, {
						method: 'DELETE',
						headers: {
							...(token ? { Authorization: `Bearer ${token}` } : {})
						}
					});
					if (!res.ok) throw new Error(`HTTP ${res.status}`);
					const nextTotal = Math.max(0, totalCount - 1);
					const nextTotalPages = Math.max(1, Math.ceil(nextTotal / perPage));
					const targetPage = Math.min(page, nextTotalPages);
					if (targetPage !== page) {
						setPage(targetPage);
					} else {
						fetchSpecs(targetPage, perPage, listFilter);
					}
					if (typeof onUpdate === 'function') onUpdate(specifications.filter(spec => spec.id !== id));
					notify('Specification record deleted successfully', { type: 'success' });
				} catch (err: any) {
					console.error('Delete spec error', err);
					notify(`Delete failed: ${err?.message || 'Unknown error'}`, { type: 'error' });
				} finally {
					setDeleteConfirmOpen(false);
					setPendingDeleteId(null);
				}
		})();
	};

	const fetchSpecificationById = useCallback(async (id: string): Promise<Specification | null> => {
		const token = localStorage.getItem('token');
		const res = await fetch(`${API_URL}/specifications/${id}`, {
			method: 'GET',
			headers: {
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
		});

		if (!res.ok) return null;
		const spec = await res.json();
		return spec as Specification;
	}, [API_URL]);

	const handleOpenDetail = async (id: string) => {
		setLoadingDetail(true);
		try {
			const spec = await fetchSpecificationById(id);
			if (!spec) {
				notify('Failed to load specification details', { type: 'error' });
				return;
			}
			setDetailSpec(spec);
		} finally {
			setLoadingDetail(false);
		}
	};

	const handleSelectImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0] || null;
		setImportFile(file);
	};

	const handleCloseImportModal = () => {
		if (importing) return;
		setImportModalOpen(false);
		setImportFile(null);
		if (importFileInputRef.current) importFileInputRef.current.value = '';
	};

	const handleConfirmImport = async () => {
		const file = importFile;
		if (!file) return;

		setImporting(true);
		try {
			const isSpecialTemplate = importTemplateType === 'special';
			const arrayBuffer = await file.arrayBuffer();
			const workbook = XLSX.read(arrayBuffer, { type: 'array' });

			const targetSheetName =
				workbook.SheetNames.find((name) => normalizeHeader(name).includes('analyticalresult')) || workbook.SheetNames[0];

			if (!targetSheetName) throw new Error('No worksheet found in uploaded file');

			const parsedSpec = parseAnalyticalResultSheet(workbook.Sheets[targetSheetName], isSpecialTemplate);
			if (!parsedSpec.batchNumber || !parsedSpec.productDescription || parsedSpec.items.length === 0) {
				throw new Error('Unable to parse required fields from Analytical Result (need Batch Number, Product Description).');
			}

			const dto = {
				BatchNumber: parsedSpec.batchNumber,
				ProductDescription: parsedSpec.productDescription,
				Manufacturer: parsedSpec.manufacturer || null,
				ProductType: parsedSpec.productType || null,
				CountryOfOrigin: parsedSpec.countryOfOrigin || null,
				SpecNumber: parsedSpec.specNumber || null,
				ReportNumber: parsedSpec.reportNumber || null,
				SampleSubmissionRefNumber: parsedSpec.sampleSubmissionRefNumber || null,
				SampleReceivedDate: parsedSpec.sampleReceivedDate || null,
				Packaging: parsedSpec.packaging || null,
				TestingPurpose: parsedSpec.testingPurpose || null,
				Client: parsedSpec.client || null,
				ManufactureDate: parsedSpec.manufactureDate || null,
				ExpiryDate: parsedSpec.expiryDate || null,
				Items: parsedSpec.items.map((item) => ({
					Parameter: item.parameter,
					Classification: item.classification,
					TestMethod: item.testMethod,
					Uom: item.uom,
					SpecMin: item.specMin ?? null,
					SpecMax: item.specMax ?? null,
					SpecMinKJ: item.specMinKJ ?? null,
					SpecMaxKJ: item.specMaxKJ ?? null,
				})),
			};

			const token = localStorage.getItem('token');
			const res = await fetch(`${API_URL}/specifications`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify(dto),
			});

			if (!res.ok) {
				const text = await res.text();
				throw new Error(text || `HTTP ${res.status}`);
			}

			notify(`Import success: ${parsedSpec.items.length} specification items imported`, { type: 'success' });
			setFormData(parsedSpec);
			setIsAdding(false);
			await fetchSpecs(page, perPage, listFilter);

			setImportModalOpen(false);
			setImportFile(null);
			if (importFileInputRef.current) importFileInputRef.current.value = '';
		} catch (error: any) {
			notify(`Import failed: ${error?.message || 'Unknown error'}`, { type: 'error' });
		} finally {
			setImporting(false);
		}
	};

	const handleCancel = () => {
		setIsAdding(false);
		setEditingId(null);
		setFormData({
			batchNumber: '',
			productDescription: '',
			specNumber: '',
			sampleSubmissionRefNumber: '',
			manufactureDate: '',
			expiryDate: '',
			items: []
		});
	};

	const formatDateCell = (value?: string | null) => {
		if (!value) return '-';
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return '-';
		if (date.getUTCFullYear() === 1970 && date.getUTCMonth() === 0 && date.getUTCDate() === 1) return '-';
		return date.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
	};

	if (detailSpec) {
		return (
			<SpecDataDetail
				spec={detailSpec}
				onBack={() => setDetailSpec(null)}
				onUpdated={(updatedSpec) => {
					setDetailSpec(updatedSpec);
					setSpecs((current) => current.map((item) => (item.id === updatedSpec.id ? updatedSpec : item)));
					if (typeof onUpdate === 'function') {
						onUpdate(specifications.map((item) => (item.id === updatedSpec.id ? updatedSpec : item)));
					}
				}}
			/>
		);
	}

	return (
		<div className="space-y-6" style={{ margin: 20 }}>
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-2xl mb-1">Specification Data Management</h2>
					<p className="text-sm text-gray-500">Manage quality standards and specifications for raw materials</p>
				</div>
				{!isAdding && (
					<div className="flex items-center gap-2">
						<Button
							type="button"
							variant="outline"
							className="border-blue-300 text-blue-700 hover:bg-blue-50"
							onClick={() => {
								setImportTemplateType('normal');
								setImportFile(null);
								setImportModalOpen(true);
							}}
							disabled={importing}
						>
							<Upload className="mr-2 size-4" />
							Import
						</Button>
					</div>
				)}
			</div>

			{importModalOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
					<div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl">
						<div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
							<h3 className="text-lg font-semibold text-slate-900">Import Specification</h3>
							<Button type="button" variant="ghost" onClick={handleCloseImportModal}>
								Close
							</Button>
						</div>

						<div className="space-y-4 px-5 py-4">
							<div className="space-y-2">
								<Label className="text-sm text-slate-700">1. Select Spec Type</Label>
								<select
									className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
									value={importTemplateType}
									onChange={(e) => setImportTemplateType(e.target.value as SpecImportType)}
									disabled={importing}
								>
									<option value="normal">Normal Spec</option>
									<option value="special">Special Spec</option>
								</select>
								<p className="text-xs text-slate-500">
									{importTemplateType === 'special'
										? 'Special spec: metadata from first 12 rows; includes Spec Min/Max (/100 kJ) columns.'
										: 'Normal spec: standard Analytical Result layout.'}
								</p>
							</div>

							<div className="space-y-2">
								<Label className="text-sm text-slate-700">2. Choose File</Label>
								<input
									ref={importFileInputRef}
									type="file"
									accept=".xlsx,.xls"
									className="sr-only"
									onChange={handleSelectImportFile}
									disabled={importing}
								/>
								<div className="flex items-center gap-2">
									<Button
										type="button"
										variant="outline"
										onClick={() => importFileInputRef.current?.click()}
										disabled={importing}
									>
										Choose File
									</Button>
									<span className="text-sm text-slate-600">
										{importFile ? importFile.name : 'No file selected'}
									</span>
								</div>
							</div>
						</div>

						<div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
							<Button
								type="button"
								variant="ghost"
								onClick={handleCloseImportModal}
								disabled={importing}
							>
								Cancel
							</Button>
							<Button
								type="button"
								className="bg-blue-600 text-white hover:bg-blue-700"
								onClick={handleConfirmImport}
								disabled={importing || !importFile}
							>
								{importing ? 'Importing...' : 'Import and Parse'}
							</Button>
						</div>
					</div>
				</div>
			)}

			{isAdding && (
				<div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
					<div className="flex items-center justify-between pb-4 border-b">
						<h3 className="text-xl">{editingId ? 'Edit Specification' : 'New Specification'}</h3>
						<span className="text-sm text-gray-500">Fill in specification master information</span>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<div>
							<Label htmlFor="batchNumber" className="text-sm text-gray-700">Batch Number</Label>
							<Input id="batchNumber" value={formData.batchNumber} onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })} placeholder="e.g. BN-20260418-01" className="mt-1" />
						</div>
						<div>
							<Label htmlFor="productDescription" className="text-sm text-gray-700">Product Description</Label>
							<Input id="productDescription" value={formData.productDescription} onChange={(e) => setFormData({ ...formData, productDescription: e.target.value })} placeholder="e.g. Microcrystalline Cellulose" className="mt-1" />
						</div>
						<div>
										<Label htmlFor="specNumber" className="text-sm text-gray-700">Spec Number</Label>
										<Input id="specNumber" value={formData.specNumber || ''} onChange={(e) => setFormData({ ...formData, specNumber: e.target.value })} placeholder="e.g. SPEC-001" className="mt-1" />
						</div>
						<div>
										<Label htmlFor="sampleSubmissionRefNumber" className="text-sm text-gray-700">Sample Submission Ref Number</Label>
										<Input id="sampleSubmissionRefNumber" value={formData.sampleSubmissionRefNumber || ''} onChange={(e) => setFormData({ ...formData, sampleSubmissionRefNumber: e.target.value })} placeholder="e.g. SSR-20260418-09" className="mt-1" />
						</div>
						<div>
							<Label htmlFor="manufactureDate" className="text-sm text-gray-700">Manufacture Date</Label>
							<Input id="manufactureDate" type="date" value={formData.manufactureDate ? formData.manufactureDate.slice(0, 10) : ''} onChange={(e) => setFormData({ ...formData, manufactureDate: e.target.value })} className="mt-1" />
						</div>
						<div>
							<Label htmlFor="expiryDate" className="text-sm text-gray-700">Expiry Date</Label>
							<Input id="expiryDate" type="date" value={formData.expiryDate ? formData.expiryDate.slice(0, 10) : ''} onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })} className="mt-1" />
						</div>
					</div>

					{!editingId && (
					<div className="space-y-4">
						<h4 className="text-base text-gray-700 pt-2">Test Items</h4>
						<div className="bg-gray-50 rounded-lg p-4 space-y-3">
							<div className="grid grid-cols-7 gap-3 items-end">
								<div>
									<Label htmlFor="parameter" className="text-xs text-gray-600">Parameter</Label>
									<Input id="parameter" value={newItem.parameter} onChange={(e) => setNewItem({ ...newItem, parameter: e.target.value })} placeholder="e.g. Protein" className="mt-1" />
								</div>
								<div>
									<Label htmlFor="classification" className="text-xs text-gray-600">Classification</Label>
									<Input id="classification" value={newItem.classification} onChange={(e) => setNewItem({ ...newItem, classification: e.target.value })} placeholder="e.g. Mineral" className="mt-1" />
								</div>
								<div>
									<Label htmlFor="testMethod" className="text-xs text-gray-600">Test Method</Label>
									<Input id="testMethod" value={newItem.testMethod} onChange={(e) => setNewItem({ ...newItem, testMethod: e.target.value })} placeholder="e.g. HPLC" className="mt-1" />
								</div>
								<div>
									<Label htmlFor="uom" className="text-xs text-gray-600">UOM</Label>
									<Input id="uom" value={newItem.uom} onChange={(e) => setNewItem({ ...newItem, uom: e.target.value })} placeholder="e.g. %" className="mt-1" />
								</div>
								<div>
									<Label htmlFor="specMin" className="text-xs text-gray-600">Spec Min</Label>
									<Input id="specMin" type="text" value={newItem.specMin ?? ''} onChange={(e) => setNewItem({ ...newItem, specMin: e.target.value.trim() ? e.target.value.trim() : null })} placeholder="Min value or text" className="mt-1" />
								</div>
								<div>
									<Label htmlFor="specMax" className="text-xs text-gray-600">Spec Max</Label>
									<Input id="specMax" type="text" value={newItem.specMax ?? ''} onChange={(e) => setNewItem({ ...newItem, specMax: e.target.value.trim() ? e.target.value.trim() : null })} placeholder="Max value or text" className="mt-1" />
								</div>
								<Button onClick={handleAddItem} variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50">
									<Plus className="size-4 mr-1" />
									Add
								</Button>
							</div>

							<div className="overflow-x-auto mt-4">
								<table className="w-full">
									<thead>
										<tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
											<th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Parameter</th>
											<th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Classification</th>
											<th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Test Method</th>
											<th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">UOM</th>
											<th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Spec Min</th>
											<th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Spec Max</th>
											<th className="px-4 py-3 text-center text-xs text-gray-600 uppercase tracking-wider">Actions</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-gray-100">
										{formData.items.map((item, index) => (
											<tr key={index} className="hover:bg-gray-50">
												<td className="px-4 py-3 text-sm text-gray-900">{item.parameter}</td>
												<td className="px-4 py-3 text-sm text-gray-600">{item.classification}</td>
												<td className="px-4 py-3 text-sm text-gray-600">{item.testMethod}</td>
												<td className="px-4 py-3 text-sm text-gray-600">{item.uom}</td>
												<td className="px-4 py-3 text-sm text-gray-600">{item.specMin ?? '-'}</td>
												<td className="px-4 py-3 text-sm text-gray-600">{item.specMax ?? '-'}</td>
												<td className="px-4 py-3 text-center">
													<Button variant="ghost" size="sm" onClick={() => handleRemoveItem(index)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
														<Trash2 className="size-4" />
													</Button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>
					)}

					<div className="flex items-center justify-end gap-3 pt-4 border-t">
						<Button variant="outline" onClick={handleCancel} disabled={saving}>
							<X className="size-4 mr-2" />
							Cancel
						</Button>
						<Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
							<Save className="size-4 mr-2" />
							{saving ? 'Saving...' : 'Save'}
						</Button>
					</div>
				</div>
			)}

			{!isAdding && (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
					<div className="border-b border-gray-200 bg-slate-50 px-4 py-3">
						<div className="flex flex-wrap items-center gap-2">
							<Label htmlFor="list-batch-number" className="text-sm text-gray-700">Batch Number</Label>
							<Input
								id="list-batch-number"
								value={batchNumberQuery}
								onChange={(e) => setBatchNumberQuery(e.target.value)}
								placeholder="Search batch number"
								className="w-full max-w-xs bg-white"
							/>
						</div>
					</div>
					<div>
						<table className="w-full table-fixed">
							<thead>
								<tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
									<th className="w-[10%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Batch Number</th>
									<th className="w-[16%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Product Description</th>
									<th className="w-[14%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Manufacturer</th>
									<th className="w-[11%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Manufacture Date</th>
									<th className="w-[12%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Spec Number</th>
									<th className="w-[11%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Expiry Date</th>
									<th className="w-[16%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Sample Submission Ref Number</th>
									<th className="w-[20%] px-4 py-3 text-center text-xs text-gray-600 uppercase tracking-wider">Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-100">
								{specs.map((spec) => (
									<tr key={spec.id} className="hover:bg-gray-50 transition-colors">
										<td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={spec.batchNumber}>{spec.batchNumber}</span></td>
										<td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={spec.productDescription}>{spec.productDescription}</span></td>
										<td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={spec.manufacturer || ''}>{spec.manufacturer || '-'}</span></td>
										<td className="px-4 py-3 text-sm text-gray-600">{formatDateCell(spec.manufactureDate)}</td>
										<td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={spec.specNumber || ''}>{spec.specNumber || '-'}</span></td>
										<td className="px-4 py-3 text-sm text-gray-600">{formatDateCell(spec.expiryDate)}</td>
										<td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={spec.sampleSubmissionRefNumber || ''}>{spec.sampleSubmissionRefNumber || '-'}</span></td>
										<td className="px-4 py-3">
											<div className="flex flex-row gap-1 justify-center">
												<button className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md transition-colors" onClick={() => handleOpenDetail(spec.id)} disabled={loadingDetail}>View</button>
												<button className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-100 rounded-md transition-colors" onClick={() => handleEditSpecification(spec)}>Edit</button>
												<button className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold text-red-700 border border-red-200 hover:bg-red-50 rounded-md transition-colors" onClick={() => handleDeleteClick(spec.id)}>Delete</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{!loadingList && specs.length === 0 && !isAdding && (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
					<FileText className="size-16 mx-auto text-gray-300 mb-4" />
					<h3 className="text-lg text-gray-600 mb-2">No specifications yet</h3>
					<p className="text-sm text-gray-400 mb-6">Please import specification data from Excel.</p>
				</div>
			)}

			<Confirm
				isOpen={deleteConfirmOpen}
				loading={false}
				title="Confirm Delete"
				content="Are you sure you want to delete this specification record?"
				onConfirm={handleConfirmDelete}
				onClose={() => {
					setDeleteConfirmOpen(false);
					setPendingDeleteId(null);
				}}
			/>
		</div>
	);
}
